/**
 * catalogueCoverage.js — RC 1.26.B.2.1
 *
 * Pure helpers that join the employer catalogue (from
 * `/api/admin/catalogue`) with the already-seeded `job_sources` rows
 * (from `/api/admin/sources` + `/api/admin/sources/freshness`) to
 * produce a per-catalogue-row "coverage" verdict:
 *
 *   { matched: boolean,
 *     match_type: 'detected_from_url' | 'name_family' | null,
 *     source_id?: string,
 *     source_name?: string,
 *     source_type?: string,
 *     enabled?: boolean,
 *     active_jobs?: number }
 *
 * Matching rules (ordered; stop at first hit):
 *
 *   1. `job_sources.config_json._detected_from_url === careers_url`
 *      — the exact key the Catalogue Seed flow stores on the row.
 *      High-confidence, URL-identical; `match_type='detected_from_url'`.
 *
 *   2. Fallback: source_type equals the catalogue row's
 *      suggested_adapter (or detected_family) AND the employer names
 *      overlap (substring either direction, case-insensitive, after
 *      stripping generic suffixes like "Careers" / "Inc" / "Corp").
 *      `match_type='name_family'`.
 *
 * We deliberately DO NOT soft-match on catalogue rows that are
 * `pending_family_adapter` or `bespoke_required` — there is no
 * supported adapter to match against. For those rows we return
 * `{matched:false, match_type:null}` regardless of any coincidentally
 * named `job_sources` row.
 */

/**
 * Normalise an employer display name for case-insensitive substring
 * comparison. Strips common suffixes and punctuation but preserves
 * internal word boundaries. Returns an empty string for nullish input.
 */
export function normaliseEmployerName(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .toLowerCase()
    .replace(/[\u2018\u2019\u201C\u201D]/g, '')          // smart quotes
    .replace(/[^a-z0-9\s&]/g, ' ')
    .replace(/\b(careers?|jobs?|inc|incorporated|corp|corporation|co|company|ltd|limited|plc|llc|llp|group|holdings?|the|and|&)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Return the family token used for matching. Catalogue rows store
 * `suggested_adapter` (e.g. "workday_board") plus `detected_family`
 * (e.g. "workday"). `job_sources.source_type` is always the adapter
 * form. Prefer the suggested_adapter when present.
 */
function catalogueAdapter(row) {
  if (!row) return null;
  if (row.suggested_adapter) return row.suggested_adapter;
  const fam = row.detected_family;
  if (!fam) return null;
  // Already an adapter-style token? leave as-is.
  if (fam.endsWith('_board')) return fam;
  return null;
}

/**
 * Build a Map keyed by normalised `_detected_from_url` for O(1)
 * lookups during the URL-first pass.
 */
function indexSourcesByDetectedUrl(sources) {
  const map = new Map();
  for (const s of sources || []) {
    const url = s?.config_json?._detected_from_url;
    if (typeof url === 'string' && url) {
      map.set(url, s);
    }
  }
  return map;
}

/**
 * Decide whether two employer names should be considered the same
 * employer for the fallback match. Returns true when either
 * normalised name is a substring of the other AND the shorter
 * normalised name is at least 3 chars (guards against one-letter
 * collisions).
 */
export function namesOverlap(a, b) {
  const na = normaliseEmployerName(a);
  const nb = normaliseEmployerName(b);
  if (!na || !nb) return false;
  const short = na.length <= nb.length ? na : nb;
  const long = na.length <= nb.length ? nb : na;
  if (short.length < 3) return false;
  return long.includes(short);
}

/**
 * Resolve coverage for a single catalogue row. See module docstring
 * for the ordered matching rules. Only `seedable_now` rows are
 * considered — everything else always returns matched=false.
 */
export function resolveCoverage(catalogueRow, sources, freshnessById) {
  const blank = { matched: false, match_type: null };
  if (!catalogueRow || catalogueRow.status_bucket !== 'seedable_now') {
    return blank;
  }
  if (!Array.isArray(sources) || sources.length === 0) return blank;

  // 1. URL-exact match.
  const urlMap = indexSourcesByDetectedUrl(sources);
  const viaUrl = catalogueRow.careers_url && urlMap.get(catalogueRow.careers_url);
  if (viaUrl) {
    const fr = freshnessById?.[viaUrl.id] || null;
    return {
      matched: true,
      match_type: 'detected_from_url',
      source_id: viaUrl.id,
      source_name: viaUrl.name,
      source_type: viaUrl.source_type,
      enabled: viaUrl.enabled,
      active_jobs: fr?.active_jobs ?? null,
    };
  }

  // 2. Family + name fallback.
  const adapter = catalogueAdapter(catalogueRow);
  if (!adapter) return blank;
  const candidates = sources.filter((s) => s.source_type === adapter);
  for (const s of candidates) {
    if (namesOverlap(catalogueRow.company_name, s.name)) {
      const fr = freshnessById?.[s.id] || null;
      return {
        matched: true,
        match_type: 'name_family',
        source_id: s.id,
        source_name: s.name,
        source_type: s.source_type,
        enabled: s.enabled,
        active_jobs: fr?.active_jobs ?? null,
      };
    }
  }
  return blank;
}

/**
 * Compose a map keyed by catalogue `careers_url` → coverage verdict
 * for the entire catalogue. Tiny helper so the React page stays
 * declarative.
 */
export function buildCoverageMap(catalogueItems, sources, freshnessItems) {
  const freshnessById = {};
  for (const f of freshnessItems || []) {
    if (f && f.id) freshnessById[f.id] = f;
  }
  const out = {};
  for (const row of catalogueItems || []) {
    if (!row || !row.careers_url) continue;
    out[row.careers_url] = resolveCoverage(row, sources, freshnessById);
  }
  return out;
}
